// ── DatePicker ──────────────────────────────────────────────────────────────
// Theme-aware day picker. Replaces native <input type="date">. Returns
// ISO 'YYYY-MM-DD' (or '' for empty).
//
// Same general contract as MonthPicker: trigger button + popover, ESC + click-
// outside to close, tabular numerals, semantic Material 3 tokens. Day grid
// with German Mon..Son weekday labels and chevron-style year navigation.
//
// Optional `min`/`max` (ISO 'YYYY-MM-DD') greys out unselectable days. If
// omitted, every date 1900-01-01 .. 2099-12-31 is selectable.
//
// Portal mode: when the trigger lives inside a Modal or a section with a
// transform-based stacking context (like .mc-anim-fade-up), set usePortal=
// true so the popover escapes the clip + paints above sibling sections.
// menuZIndex bumps the stacking order — set 100001 inside Modals.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { registerMenuEscape } from './overlayStack'

const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]
const WEEKDAY_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

export interface DatePickerProps {
  /** '' for empty, otherwise 'YYYY-MM-DD' */
  value: string
  onChange: (next: string) => void
  label?: string
  ariaLabel?: string
  placeholder?: string
  clearLabel?: string
  /** Optional inclusive bounds. ISO 'YYYY-MM-DD'. */
  min?: string
  max?: string
  disabled?: boolean
  /** size='compact' shrinks paddings & font for inline filter rows. size='touch' = every target at least 44 px (M30 QM Leitung re-skin, touch rule). */
  size?: 'default' | 'compact' | 'touch'
  className?: string
  /** Render the popover into document.body. Set TRUE when the trigger lives
   *  in a Modal, a transform/animation container, or any clipping parent. */
  usePortal?: boolean
  /** Z-index for the popover (used both in-flow and portalled). Default 50.
   *  Bump above 100000 when the trigger lives inside a Modal/Dialog. */
  menuZIndex?: number
}

/** Compare ISO YYYY-MM-DD lexicographically (safe — fixed format). */
function inRange(iso: string, min?: string, max?: string): boolean {
  if (min && iso < min) return false
  if (max && iso > max) return false
  return true
}

function pad(n: number): string { return n.toString().padStart(2, '0') }
function isoOf(y: number, m: number, d: number): string { return `${y}-${pad(m + 1)}-${pad(d)}` }
/** Format ISO YYYY-MM-DD as German DD.MM.YYYY. Tolerant of non-string input. */
function fmtDe(iso: unknown): string {
  if (iso == null) return ''
  const s = typeof iso === 'string' ? iso : String(iso)
  if (!s) return ''
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s
}

export function DatePicker({
  value,
  onChange,
  label,
  ariaLabel = 'Datum wählen',
  placeholder = 'Datum wählen…',
  clearLabel = 'Löschen',
  min,
  max,
  disabled = false,
  size = 'default',
  className = '',
  usePortal = false,
  menuZIndex = 50,
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [portalCoords, setPortalCoords] = useState<{ top: number; left: number; width: number } | null>(null)

  const today = new Date()
  const parsedYear  = value ? parseInt(value.slice(0, 4), 10) : today.getFullYear()
  const parsedMonth = value ? parseInt(value.slice(5, 7), 10) - 1 : today.getMonth()

  const [displayYear, setDisplayYear]   = useState(parsedYear)
  const [displayMonth, setDisplayMonth] = useState(parsedMonth)

  // Sync displayed month when value changes externally
  useEffect(() => {
    if (value) {
      setDisplayYear(parseInt(value.slice(0, 4), 10))
      setDisplayMonth(parseInt(value.slice(5, 7), 10) - 1)
    }
  }, [value])

  // Click-outside — also dismiss when click lands inside the portalled popover
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      const insideTrigger = ref.current?.contains(target)
      const insidePopover = popoverRef.current?.contains(target)
      if (!insideTrigger && !insidePopover) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Escape schließt NUR die Auswahl, nie den Drawer/das Modal darum (overlayStack: die offene Auswahl steht obenauf).
  useEffect(() => (open ? registerMenuEscape(() => setOpen(false)) : undefined), [open])

  // When portalled, anchor the popover under the trigger using fixed coords.
  // Recompute on open + on scroll/resize while open.
  useLayoutEffect(() => {
    if (!open || !usePortal || !triggerRef.current) return
    const reposition = () => {
      const rect = triggerRef.current!.getBoundingClientRect()
      const POPOVER_H = 360 // approx — datepicker is ~340-360px tall
      const VIEW = window.innerHeight
      const spaceBelow = VIEW - rect.bottom
      const placeAbove = spaceBelow < POPOVER_H && rect.top > POPOVER_H
      setPortalCoords({
        top:  placeAbove ? Math.max(8, rect.top - POPOVER_H - 4) : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 260),
      })
    }
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, usePortal])

  // Build day grid for displayMonth (always 6 weeks = 42 cells)
  const firstOfMonth = new Date(displayYear, displayMonth, 1)
  const dowOf1st = (firstOfMonth.getDay() + 6) % 7   // Monday-first (0..6)
  const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate()
  const daysInPrev  = new Date(displayYear, displayMonth, 0).getDate()

  type Cell = { iso: string; day: number; outOfMonth: boolean; disabled: boolean; isToday: boolean; isSelected: boolean }
  const cells: Cell[] = []
  // Leading days from previous month
  const prevMonth = displayMonth === 0 ? 11 : displayMonth - 1
  const prevYear  = displayMonth === 0 ? displayYear - 1 : displayYear
  for (let i = dowOf1st - 1; i >= 0; i--) {
    const d = daysInPrev - i
    const iso = isoOf(prevYear, prevMonth, d)
    cells.push({
      iso, day: d, outOfMonth: true,
      disabled: !inRange(iso, min, max),
      isToday: false,
      isSelected: false,
    })
  }
  // Current month
  const todayIso = isoOf(today.getFullYear(), today.getMonth(), today.getDate())
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = isoOf(displayYear, displayMonth, d)
    cells.push({
      iso, day: d, outOfMonth: false,
      disabled: !inRange(iso, min, max),
      isToday: iso === todayIso,
      isSelected: iso === value,
    })
  }
  // Trailing days from next month — fill to 42
  const nextMonth = displayMonth === 11 ? 0 : displayMonth + 1
  const nextYear  = displayMonth === 11 ? displayYear + 1 : displayYear
  let next = 1
  while (cells.length < 42) {
    const iso = isoOf(nextYear, nextMonth, next)
    cells.push({
      iso, day: next, outOfMonth: true,
      disabled: !inRange(iso, min, max),
      isToday: false,
      isSelected: false,
    })
    next++
  }

  function shiftMonth(delta: number) {
    let m = displayMonth + delta
    let y = displayYear
    while (m < 0) { m += 12; y -= 1 }
    while (m > 11) { m -= 12; y += 1 }
    setDisplayMonth(m)
    setDisplayYear(y)
  }

  function pick(iso: string) {
    onChange(iso)
    setOpen(false)
  }

  const touch = size === 'touch'
  const triggerCls = size === 'compact'
    ? 'px-2 py-1 text-xs rounded-md'
    : touch ? 'min-h-12 px-4 py-2 text-base rounded-xl' : 'px-3 py-2 text-sm rounded-lg'

  const display = value ? fmtDe(value) : ''

  return (
    <div ref={ref} className={`relative inline-block ${className}`} style={{ width: '100%' }}>
      {label && (
        <label className="block text-[10px] uppercase tracking-wide text-on-surface-variant mb-1">
          {label}
        </label>
      )}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`flex w-full items-center justify-between gap-2 border border-outline-variant bg-surface-container-lowest text-on-surface ${triggerCls} font-mono mc-tabular hover:border-outline disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors`}
      >
        <span className={display ? '' : 'text-on-surface-variant font-sans'}>
          {display || placeholder}
        </span>
        <span className="material-symbols-outlined !text-[18px] text-on-surface-variant">
          calendar_month
        </span>
      </button>

      {open && (() => {
        const popover = (
          <div
            ref={popoverRef}
            role="dialog"
            className={`${touch ? 'min-w-[344px] max-w-[calc(100vw-16px)]' : 'min-w-[260px]'} rounded-xl border border-outline-variant bg-surface-container-lowest shadow-xl p-3 animate-[fade-in_0.12s_ease-out]`}
            style={
              usePortal && portalCoords
                ? {
                    position: 'fixed',
                    top: portalCoords.top,
                    left: portalCoords.left,
                    width: portalCoords.width,
                    zIndex: menuZIndex,
                  }
                : { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: menuZIndex }
            }
          >
          {/* Header — month/year + nav */}
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => shiftMonth(-1)}
              aria-label="Vorheriger Monat"
              className={`${touch ? 'grid h-11 w-11 place-items-center' : 'p-1'} rounded-md text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors`}>
              <span className="material-symbols-outlined !text-[18px]">chevron_left</span>
            </button>
            <div className="flex items-center gap-1 text-sm font-bold text-on-surface">
              <span>{MONTH_NAMES_DE[displayMonth]}</span>
              <span className="mc-tabular">{displayYear}</span>
            </div>
            <button type="button" onClick={() => shiftMonth(1)}
              aria-label="Nächster Monat"
              className={`${touch ? 'grid h-11 w-11 place-items-center' : 'p-1'} rounded-md text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors`}>
              <span className="material-symbols-outlined !text-[18px]">chevron_right</span>
            </button>
          </div>

          {/* Year nav (shift ±1 year) */}
          <div className="flex items-center justify-center gap-2 mb-2 text-[10px] text-on-surface-variant">
            <button type="button" onClick={() => { setDisplayYear(y => y - 1) }}
              className={`${touch ? 'min-h-11 px-3 text-xs' : 'px-2 py-0.5'} rounded-md hover:bg-surface-container hover:text-on-surface transition-colors`}
              aria-label="Vorheriges Jahr">
              ‹ {displayYear - 1}
            </button>
            <button type="button" onClick={() => { setDisplayYear(y => y + 1) }}
              className={`${touch ? 'min-h-11 px-3 text-xs' : 'px-2 py-0.5'} rounded-md hover:bg-surface-container hover:text-on-surface transition-colors`}
              aria-label="Nächstes Jahr">
              {displayYear + 1} ›
            </button>
          </div>

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-0.5 mb-1 text-[10px] font-extrabold uppercase tracking-wider text-on-surface-variant text-center">
            {WEEKDAY_DE.map(w => <div key={w} className="py-1">{w}</div>)}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map(c => (
              <button
                key={c.iso}
                type="button"
                disabled={c.disabled}
                onClick={() => !c.disabled && pick(c.iso)}
                aria-label={fmtDe(c.iso)}
                aria-pressed={c.isSelected}
                className={`
                  ${touch ? 'h-11 min-w-11 text-sm' : 'text-[11px] py-1.5'} font-mono mc-tabular rounded-md transition-all
                  ${c.isSelected
                    ? 'bg-primary text-on-primary font-bold shadow-sm'
                    : c.isToday
                      ? 'bg-primary/15 text-primary font-bold ring-1 ring-primary/30'
                      : c.outOfMonth
                        ? 'text-outline-variant hover:text-on-surface-variant hover:bg-surface-container'
                        : 'text-on-surface hover:bg-surface-container hover:text-primary'}
                  ${c.disabled ? 'opacity-30 cursor-not-allowed hover:bg-transparent hover:text-outline-variant' : 'cursor-pointer'}
                `}
              >
                {c.day}
              </button>
            ))}
          </div>

          {/* Footer — clear + today */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-outline-variant/30">
            <button type="button" onClick={() => { onChange(''); setOpen(false) }}
              className={`${touch ? 'min-h-11 px-3 text-sm' : 'text-[11px]'} font-semibold text-on-surface-variant hover:text-error transition-colors`}>
              {clearLabel}
            </button>
            <button type="button" onClick={() => { onChange(todayIso); setOpen(false) }}
              className={`${touch ? 'min-h-11 px-3 text-sm' : 'text-[11px]'} font-semibold text-primary hover:text-primary/80 transition-colors`}>
              Heute
            </button>
          </div>
        </div>
        )
        return usePortal && portalCoords ? createPortal(popover, document.body) : popover
      })()}
    </div>
  )
}
